import { type FC, useState } from 'react';
import { useMeterModels } from 'api/hooks/meter-models/useMeterModels';
import { Table } from 'components';
import { RoleName, type SerializedMeterModel } from '@enmon/commons/lib/tools';
import type { Key, SortOrder } from 'antd/lib/table/interface';
import type { TablePaginationConfig } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { useUserInfo } from '@/hooks/useUser';
import { getMeterModelTableColumnsConfig } from '../utils/getColumnsConfig';
import { useCompatibleModelsFilterWithPagination } from '../hooks/useMeterModelsFilterWithPagination';
import { useModuleDetailContext } from '../store';

export const CompatibleMeterModelsTable: FC = () => {
  const { role } = useUserInfo() ?? {};
  const isMagicRole = role?.name === RoleName.Magic;

  // Context
  const { module } = useModuleDetailContext();

  // State
  const { filter, pagination, setPaginationState, setFilterAndPaginationState } =
    useCompatibleModelsFilterWithPagination();
  const [sortState, setSortState] = useState<{ field: Key | null; order: SortOrder }>({
    field: null,
    order: null,
  });

  // Fetch
  const { data: modelsResponse, isFetching: isFetchingModels } = useMeterModels(
    {
      ...(filter?.search && { search: filter?.search }),
      ...(filter?.commodity && { commodity: filter?.commodity }),
      ...(filter?.manufacturerId && { manufacturerId: filter?.manufacturerId }),
      ...(module && { meterModules: [module.id] }),
      populate: ['manufacturer'],
      sort: filter?.sort,
      page: pagination.current,
      pageSize: pagination.pageSize,
    },
    {
      onSuccess: data => {
        setPaginationState({ total: data?.meta?.pagination?.total });
      },
      enabled: !!module,
    },
  );
  const models = modelsResponse?.data ?? [];

  // Handlers
  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _: any,
    sorter: SorterResult<SerializedMeterModel> | SorterResult<SerializedMeterModel>[],
  ) => {
    const { columnKey, order } = (Array.isArray(sorter) ? sorter[0] : sorter) ?? {};
    const newFilter = {
      ...(columnKey &&
        order && {
          sort: { [columnKey.toString()]: `${order.slice(0, -3)}` },
        }),
    };

    setSortState({ field: columnKey, order });
    setFilterAndPaginationState(newFilter, newPagination);
  };

  // Table Config
  const tableColumns = getMeterModelTableColumnsConfig({
    isMagicRole,
    sortState,
  });

  return (
    <Table<SerializedMeterModel>
      columns={tableColumns}
      dataSource={models}
      loading={isFetchingModels}
      pagination={{
        ...pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} models`,
      }}
      rowKey={item => item.id}
      scroll={{ x: 'fit-content' }}
      onChange={handleTableChange}
    />
  );
};
